import React, { useState, forwardRef } from 'react';

const TextInput = forwardRef(function TextInput({
  label,
  type = 'text',
  value,
  onChange,
  error,
  icon,
  placeholder,
  disabled = false,
  required = false,
  className = '',
  ...props
}, ref) {
  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const hasValue = value && value.length > 0;
  const isFloating = focused || hasValue;
  const inputType = type === 'password' && showPassword ? 'text' : type;

  return (
    <div className={`relative ${className}`}>
      {/* Input container */}
      <div
        className={`
          relative bg-prism-card rounded-input border transition-all duration-200
          ${focused ? 'border-prism-cyan' : error ? 'border-prism-error' : 'border-prism-border'}
          ${disabled ? 'opacity-50' : ''}
        `}
      >
        {/* Leading icon */}
        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-prism-text-secondary">
            {icon}
          </div>
        )}

        {/* Input field */}
        <input
          ref={ref}
          type={inputType}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={isFloating ? placeholder : ''}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`
            w-full bg-transparent text-prism-text font-body
            px-4 pt-5 pb-2
            ${icon ? 'pl-12' : ''}
            ${type === 'password' ? 'pr-12' : ''}
            focus:outline-none
          `}
          {...props}
        />

        {/* Floating label */}
        <label
          className={`
            absolute left-4 transition-all duration-200 pointer-events-none
            ${icon ? 'left-12' : ''}
            ${isFloating
              ? 'top-2 text-xs text-prism-text-secondary'
              : 'top-1/2 -translate-y-1/2 text-base text-prism-text-secondary'
            }
          `}
        >
          {label}
          {required && <span className="text-prism-coral ml-1">*</span>}
        </label>

        {/* Password toggle */}
        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-prism-text-secondary hover:text-prism-text"
          >
            {showPassword ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-prism-error flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
});

export default TextInput;

// Textarea variant
export const TextArea = forwardRef(function TextArea({
  label,
  value,
  onChange,
  error,
  placeholder,
  rows = 4,
  disabled = false,
  required = false,
  className = '',
  ...props
}, ref) {
  const [focused, setFocused] = useState(false);
  const hasValue = value && value.length > 0;
  const isFloating = focused || hasValue;

  return (
    <div className={`relative ${className}`}>
      <div
        className={`
          relative bg-prism-card rounded-input border transition-all duration-200
          ${focused ? 'border-prism-cyan' : error ? 'border-prism-error' : 'border-prism-border'}
          ${disabled ? 'opacity-50' : ''}
        `}
      >
        <textarea
          ref={ref}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={isFloating ? placeholder : ''}
          rows={rows}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="w-full bg-transparent text-prism-text font-body px-4 pt-6 pb-2 focus:outline-none resize-none"
          {...props}
        />

        <label
          className={`
            absolute left-4 transition-all duration-200 pointer-events-none
            ${isFloating
              ? 'top-2 text-xs text-prism-text-secondary'
              : 'top-4 text-base text-prism-text-secondary'
            }
          `}
        >
          {label}
          {required && <span className="text-prism-coral ml-1">*</span>}
        </label>
      </div>

      {error && (
        <p className="mt-1 text-sm text-prism-error flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
});
